import React, { useEffect, useState } from 'react';
import './ReprojectCrsModal.css';

/**
 * ReprojectCrsModal — shown during upload when LAS/LAZ files aren't in the
 * project's meters CRS and the source CRS can't be auto-detected from the file
 * header, OR when the project has no meters CRS registered yet (blocked).
 *
 * Files whose source CRS IS detected (e.g. WGS84 lat/lon) are reprojected
 * automatically and never reach this modal.
 *
 * Props:
 *   open          : boolean
 *   mode          : 'ask' | 'blocked'
 *   askFiles      : [{ name, detect }]  — need a source CRS from the user
 *   autoFiles     : [{ name, detect }]  — will be handled automatically (info)
 *   blockedFiles  : [{ name, detect }]  — for 'blocked' mode (no project CRS)
 *   targetLabel   : string              — human label for the project's CRS
 *   onCancel      : () => void
 *   onContinue    : ({ sourceCrs }) => void   // sourceCrs = {epsg} | {wkt}
 */
const COMMON_SOURCE_CRS = [
  { label: 'WGS84 latitude/longitude (EPSG:4326)', epsg: 4326 },
  { label: 'NAD83 latitude/longitude (EPSG:4269)', epsg: 4269 },
  { label: 'NAD83 / Virginia North — US ft (EPSG:2284)', epsg: 2284 },
  { label: 'NAD83 / Virginia South — US ft (EPSG:2283)', epsg: 2283 },
];

const ReprojectCrsModal = ({
  open,
  mode = 'ask',
  askFiles = [],
  autoFiles = [],
  blockedFiles = [],
  targetLabel = '',
  onCancel,
  onContinue,
}) => {
  const [inputMode, setInputMode] = useState('common'); // 'common' | 'epsg' | 'wkt'
  const [commonEpsg, setCommonEpsg] = useState(String(COMMON_SOURCE_CRS[0].epsg));
  const [epsgText, setEpsgText] = useState('');
  const [wktText, setWktText] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setError('');
    }
  }, [open]);

  if (!open) return null;

  const handleContinue = () => {
    let sourceCrs = null;
    if (inputMode === 'common') {
      sourceCrs = { epsg: Number(commonEpsg) };
    } else if (inputMode === 'epsg') {
      const n = parseInt(String(epsgText).replace(/\D/g, ''), 10);
      if (!n) {
        setError('Enter a valid EPSG code (e.g. 2284).');
        return;
      }
      sourceCrs = { epsg: n };
    } else {
      const wkt = String(wktText).trim();
      if (wkt.length < 20 || !/(PROJCS|PROJCRS|GEOGCS|GEOGCRS)/i.test(wkt)) {
        setError('Paste a valid WKT coordinate-system definition.');
        return;
      }
      sourceCrs = { wkt };
    }
    onContinue({ sourceCrs });
  };

  return (
    <div className="reproj-modal-overlay" onClick={onCancel}>
      <div className="reproj-modal" onClick={(e) => e.stopPropagation()}>
        {mode === 'blocked' ? (
          <>
            <div className="reproj-modal-header reproj-modal-header--warn">
              <i className="fas fa-exclamation-triangle" />
              <h2>Set the project coordinate system first</h2>
            </div>
            <div className="reproj-modal-body">
              <p>
                These files aren't in meters and need to be converted, but this
                project has no coordinate system (WKT) registered yet. Register
                the project's CRS in the RSA panel, then upload again.
              </p>
              <ul className="reproj-file-list">
                {blockedFiles.map((f) => (
                  <li key={f.name}>
                    <span className="reproj-file-name">{f.name}</span>
                    <span className="reproj-file-detail">{f.detect?.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="reproj-modal-footer">
              <button className="reproj-btn reproj-btn--cancel" onClick={onCancel}>
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="reproj-modal-header">
              <i className="fas fa-globe" />
              <h2>Confirm the coordinate system of your point cloud</h2>
            </div>
            <div className="reproj-modal-body">
              <p>
                These files will be reprojected to the project CRS
                {targetLabel ? <> (<strong>{targetLabel}</strong>)</> : null} in
                meters, but their own coordinate system couldn't be read from the
                file. Tell us what system they're in:
              </p>
              <ul className="reproj-file-list">
                {askFiles.map((f) => (
                  <li key={f.name}>
                    <span className="reproj-file-name">{f.name}</span>
                    <span className="reproj-file-detail">{f.detect?.reason}</span>
                  </li>
                ))}
              </ul>

              <div className="reproj-choice">
                <label>
                  <input
                    type="radio"
                    checked={inputMode === 'common'}
                    onChange={() => setInputMode('common')}
                  />
                  Pick a common system
                </label>
                {inputMode === 'common' && (
                  <select value={commonEpsg} onChange={(e) => setCommonEpsg(e.target.value)}>
                    {COMMON_SOURCE_CRS.map((c) => (
                      <option key={c.epsg} value={c.epsg}>{c.label}</option>
                    ))}
                  </select>
                )}
              </div>

              <div className="reproj-choice">
                <label>
                  <input
                    type="radio"
                    checked={inputMode === 'epsg'}
                    onChange={() => setInputMode('epsg')}
                  />
                  Enter an EPSG code
                </label>
                {inputMode === 'epsg' && (
                  <input
                    type="text"
                    placeholder="e.g. 2284"
                    value={epsgText}
                    onChange={(e) => setEpsgText(e.target.value)}
                  />
                )}
              </div>

              <div className="reproj-choice">
                <label>
                  <input
                    type="radio"
                    checked={inputMode === 'wkt'}
                    onChange={() => setInputMode('wkt')}
                  />
                  Paste a WKT definition
                </label>
                {inputMode === 'wkt' && (
                  <textarea
                    rows={4}
                    placeholder='PROJCS["...", GEOGCS[...], ...]'
                    value={wktText}
                    onChange={(e) => setWktText(e.target.value)}
                  />
                )}
              </div>

              {autoFiles.length > 0 && (
                <p className="reproj-auto-note">
                  {autoFiles.length} other file(s) were auto-detected and will be
                  reprojected automatically.
                </p>
              )}
              {error && <p className="reproj-error">{error}</p>}
            </div>
            <div className="reproj-modal-footer">
              <button className="reproj-btn reproj-btn--cancel" onClick={onCancel}>
                Cancel upload
              </button>
              <button className="reproj-btn reproj-btn--confirm" onClick={handleContinue}>
                Reproject &amp; upload
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReprojectCrsModal;
